/**
 * BPA Findings Service
 *
 * Isolates all network I/O for the BPA Findings feature.
 * Components and hooks should call these functions rather than
 * calling `callApi` directly, keeping API concerns out of UI logic.
 */

import callApi from 'lib/callApi';

import {
  BPAAssigneeOption,
  BPABulkAssignPayload,
  BPABulkRemoveAssigneePayload,
  BPAControlIgnorePayload,
  BPAControlIgnoreResponse,
  BPAFindingsListingResponse,
  BPAFindingsQueryParams,
  BPAFindingStatusHistoryResponse,
  BPAFindingPermissionsResponse,
  BPAIgnorePayload,
  BPAIgnoreResponse,
  BPAMarkNotesReadPayload,
  BPANoteCreatePayload,
  BPANotesResponse,
  BPANoteUpdatePayload,
  BPARecheckPayload,
  BPARecheckResponse,
  BPAWatcherMutationPayload,
  BPAWatchersResponse,
} from '../types/bpaFindings.types';

/** GET /bpa/findings/ — fetch paginated BPA finding records */
export async function fetchBPAFindings(
  params: BPAFindingsQueryParams,
): Promise<BPAFindingsListingResponse> {
  const response = await callApi('bpa/findings/', 'GET', params);
  return response.data as BPAFindingsListingResponse;
}

/** GET /bpa/findings/insights/ — fetch aggregated finding insights */
export async function fetchBPAFindingsInsights(
  params: Record<string, unknown>,
) {
  return callApi('bpa/findings/insights/', 'GET', params);
}

/** GET /bpa/findings/:id/ — fetch a single finding detail */
export async function fetchBPAFindingDetail(
  findingId: number,
  companyId: string | number,
) {
  return callApi(`bpa/findings/${findingId}/`, 'GET', {
    company_id: companyId,
  });
}

/** POST /bpa/findings/:id/permissions/refresh/ - return valid cached permissions or refresh them from T12 */
export async function refreshBPAFindingPermissions(
  findingId: number,
  companyId: string | number,
): Promise<BPAFindingPermissionsResponse> {
  const response = await callApi(
    `bpa/findings/${findingId}/permissions/refresh/`,
    'POST',
    { company_id: companyId },
    {},
  );
  return response.data as BPAFindingPermissionsResponse;
}

/** GET /bpa/findings/:id/status-history/ — fetch friendly BPA status-history rows */
export async function fetchBPAFindingStatusHistory(
  findingId: number,
  companyId: string | number,
): Promise<BPAFindingStatusHistoryResponse> {
  const response = await callApi(
    `bpa/findings/${findingId}/status-history/`,
    'GET',
    {
      company_id: companyId,
    },
  );
  return response.data as BPAFindingStatusHistoryResponse;
}

/** GET /bpa/integrations/options/ — fetch dynamic filter options (providers, modules, regions, benchmarks) */
export async function fetchBPAFindingsOptions(
  companyId: string | number,
  models: string,
) {
  return callApi('bpa/integrations/options/', 'GET', {
    company_id: companyId,
    models: models,
  });
}

/** GET /profiles/colleague/new/ — fetch active company members assignable to BPA findings */
export async function fetchBPAAssigneeOptions(
  companyId: string | number,
): Promise<BPAAssigneeOption[]> {
  const response = await callApi(
    `/profiles/colleague/new/?search=&is_active=True&nutshell=True&action=assignee&company_id=${companyId}`,
    'GET',
    {},
  );

  if (response.status === 200 || response.status === 201) {
    return response.data?.results ?? [];
  }

  return [];
}

/** GET /profiles/colleague/new/ - fetch active company members usable as watchers */
export async function fetchBPAWatcherOptions(
  companyId: string | number,
): Promise<BPAAssigneeOption[]> {
  const response = await callApi(
    `/profiles/colleague/new/?search=&is_active=True&nutshell=True&company_id=${companyId}`,
    'GET',
    {},
  );

  if (response.status === 200 || response.status === 201) {
    return response.data?.results ?? [];
  }

  return [];
}

/** POST /bpa/finding-assignees/bulk_assign/ — assign BPA findings */
export async function bulkAssignBPAFindings(payload: BPABulkAssignPayload) {
  return callApi('bpa/finding-assignees/bulk_assign/', 'POST', {}, payload);
}

/** POST /bpa/finding-assignees/bulk_remove/ — remove BPA finding assignments */
export async function bulkRemoveBPAFindingAssignments(
  payload: BPABulkRemoveAssigneePayload,
) {
  return callApi('bpa/finding-assignees/bulk_remove/', 'POST', {}, payload);
}

/** GET /bpa/finding-watchers/ - list BPA finding watchers */
export async function fetchBPAFindingWatchers(params: {
  company_id: string | number;
  finding_ids?: string;
  watcher_ids?: string;
  page?: number;
  page_size?: number;
}): Promise<BPAWatchersResponse> {
  const response = await callApi('bpa/finding-watchers/', 'GET', params);
  return response.data as BPAWatchersResponse;
}

/** POST /bpa/finding-watchers/bulk_assign/ - add BPA finding watchers */
export async function bulkAssignBPAFindingWatchers(
  payload: BPAWatcherMutationPayload,
) {
  return callApi('bpa/finding-watchers/bulk_assign/', 'POST', {}, payload);
}

/** POST /bpa/finding-watchers/bulk_remove/ - remove BPA finding watchers */
export async function bulkRemoveBPAFindingWatchers(
  payload: BPAWatcherMutationPayload,
) {
  return callApi('bpa/finding-watchers/bulk_remove/', 'POST', {}, payload);
}

/** GET /bpa/finding-notes/ - list BPA finding notes */
export async function fetchBPAFindingNotes(params: {
  company_id: string | number;
  finding_id?: number;
  finding_ids?: string;
  author_id?: number;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}): Promise<BPANotesResponse> {
  const response = await callApi('bpa/finding-notes/', 'GET', params);
  return response.data as BPANotesResponse;
}

/** POST /bpa/finding-notes/ - create a BPA finding note */
export async function createBPAFindingNote(payload: BPANoteCreatePayload) {
  return callApi('bpa/finding-notes/', 'POST', {}, payload);
}

/** PATCH /bpa/finding-notes/:id/ - update a BPA finding note */
export async function updateBPAFindingNote(
  noteId: number,
  payload: BPANoteUpdatePayload,
) {
  return callApi(`bpa/finding-notes/${noteId}/`, 'PATCH', {}, payload);
}

/** DELETE /bpa/finding-notes/:id/ - delete a BPA finding note */
export async function deleteBPAFindingNote(
  noteId: number,
  companyId: string | number,
) {
  return callApi(
    `bpa/finding-notes/${noteId}/`,
    'DELETE',
    {},
    {
      company_id: companyId,
    },
  );
}

/** POST /bpa/finding-notes/mark_all_as_read/ - mark BPA finding notes read */
export async function markBPAFindingNotesAsRead(
  payload: BPAMarkNotesReadPayload,
) {
  return callApi('bpa/finding-notes/mark_all_as_read/', 'POST', {}, payload);
}

/** POST /bpa/finding-items/ignore/ - ignore or unignore selected BPA finding items */
export async function ignoreBPAFindingItems(
  payload: BPAIgnorePayload,
): Promise<BPAIgnoreResponse> {
  const response = await callApi(
    'bpa/finding-items/ignore/',
    'POST',
    {},
    payload,
  );
  return response.data as BPAIgnoreResponse;
}

/** POST /bpa/findings/ignore/ - ignore or unignore T12 parent findings */
export async function ignoreBPAFindings(
  payload: BPAIgnorePayload,
): Promise<BPAIgnoreResponse> {
  const response = await callApi('bpa/findings/ignore/', 'POST', {}, payload);
  return response.data as BPAIgnoreResponse;
}

/** POST /bpa/controls/ignore/ - ignore active findings for a dashboard control */
export async function ignoreBPAControlFindings(
  payload: BPAControlIgnorePayload,
): Promise<BPAControlIgnoreResponse> {
  const response = await callApi('bpa/controls/ignore/', 'POST', {}, payload);
  return response.data as BPAControlIgnoreResponse;
}

/** POST /bpa/findings/:id/recheck/ - initiate a BPA finding recheck scan */
export async function recheckBPAFinding(
  findingId: number,
  payload: BPARecheckPayload,
): Promise<BPARecheckResponse> {
  const response = await callApi(
    `bpa/findings/${findingId}/recheck/`,
    'POST',
    {},
    payload,
  );
  return response.data as BPARecheckResponse;
}
